import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectModule } from '@angular/material/select';
import { ModalPdfComponent } from '../modal-pdf/modal-pdf.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpRequestService } from '../../../utilities/http/request.utilities';
import { environment } from '../../../../environments/environment';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { UserState } from '../../../store/user/user.model';
import { firstValueFrom } from 'rxjs';
import * as pdfjsLib from 'pdfjs-dist';

@Component({
  selector: 'app-upload-file',
  standalone: true,
  imports: [MatSelect, MatFormField, FormsModule, MatIcon, MatOption, MatLabel, CommonModule,
   MatProgressSpinnerModule, MatCheckboxModule, MatIconModule, MatListModule],
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.css',
})
export class UploadFileComponent {
  selectedFile: File | null = null;
  selectedCategory: string | null = null;
  fileSelected = false;
  uploadInProgress = false;
  uploadCompleted = false;
  uploadError = false;
  uploadDate: string | null = null;
  uploadedURL: string | null = null;
  reuseExistingPdf = false;
  selectedExistingPdf: any = null;
  checkingExistingFiles = false;
  validationMessage: string | null = null;
  validatingPdf = false;

  categories: string[] = []

  constructor(private dialog: MatDialog, 
    private httpRequestService: HttpRequestService,
    private http: HttpClient,
    private dialogRef: MatDialogRef<UploadFileComponent>,
    private store: Store<{ user: UserState }>,
    @Inject(MAT_DIALOG_DATA) public data: {
      make: string;
      model: string;
      serialRanges: { from: string; to: string }[];
      fileType: string;
      serialNumbers: string;
      pvc: string;
      serialExclusions: string;
      existingResources: any[];
    }) {
      this.fetchCategories();
    }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
      this.fileSelected = true;
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.uploadDate = new Date().toLocaleDateString();
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.fileSelected = true;
    }
  }

  async uploadFile(): Promise<void> {
    this.validationMessage = null;
    const user = await this.getAuthenticatedUsername();
    if (!this.selectedCategory) {
      this.validationMessage = 'Please select a category.';
      return;
    }

    if (this.uploadCompleted || this.uploadInProgress) {
      this.closeModal();
      return;
    }

    if (this.reuseExistingPdf) {
      const existingPdf = this.selectedExistingPdf[0];
      if (!existingPdf) {
        this.validationMessage = 'No existing PDF found for selected category.';
        return;
      }

      const { make, model, serialRanges, pvc, serialNumbers, serialExclusions } = this.data;
      const file_type = this.selectedCategory;
      const locale = existingPdf.locale || 'en_US';
      const file_name = existingPdf.file_name
      ? existingPdf.file_name.endsWith('.pdf') 
        ? existingPdf.file_name
        : existingPdf.file_name + '.pdf'
      : '';

      try {
        for (const range of serialRanges) {
          const filteredExclusions = (Array.isArray(serialExclusions) ? serialExclusions : []).filter((exclusion) => {
            if (typeof exclusion !== 'string') return false;
            if (exclusion.includes('-')) {
              const [start, end] = exclusion.split('-');
              return start && end && start >= range.from && end <= range.to;
            }
            return exclusion >= range.from && exclusion <= range.to;
          });

          const urlParams = new URLSearchParams({
            make,
            model,
            file_name,
            start_serial: range.from,
            end_serial: range.to,
            file_type,
            locale,
            pvc,
            serialNumbers,
            serialExclusions: JSON.stringify(filteredExclusions),
            user
          });

          const urlWithParams = `${environment.integrations.manualUpload}?${urlParams.toString()}`;

          await new Promise<any>((resolve, reject) => {
            this.httpRequestService.request<any>('GET', urlWithParams).subscribe({
              next: resolve,
              error: reject,
            });
          });
        }

        this.uploadCompleted = true;
        this.uploadDate = new Date().toLocaleDateString();
        this.uploadedURL = existingPdf.url;this.dialogRef.close(true);
        this.dialogRef.close(true);
      } catch (error) {
        console.error('Error reusing PDF:', error);
        this.uploadError = true;
      }

      return;
    }

    if (!this.selectedFile) {
      this.validationMessage = 'Please select a file before uploading.';
      return;
    }

    const validationStartTime = new Date().toISOString(); // Log start time
    const isPdfValid = await this.validatePdf(this.selectedFile);
    const validationEndTime = new Date().toISOString(); // Log end time

    console.log('PDF validation started at:', validationStartTime);
    console.log('PDF validation ended at:', validationEndTime);

    if (!isPdfValid) {

      this.updateManualUploadStatus({
        object_key: this.selectedFile?.name || '',
        manual_type: this.selectedCategory!,
        uploaded_by: user,
        size_classifier: '',
        status_field: 'corruption_error',
        start_time: validationStartTime,
        end_time: validationEndTime,
        step: 'manual_corruption_check',
        mode: 'admin frontend upload',
      });

      this.uploadInProgress = false;
      return;
    }

    if (this.isFileAlreadyUploaded(this.selectedFile)) {
      const existingFile = this.data.existingResources.find(res =>
          res.file_name === this.selectedFile?.name &&
          res.file_size === this.selectedFile?.size
      );

      if (existingFile) {
            const confirmed = confirm(`The file "${this.selectedFile.name}" already exists in the system.Do you want to use the existing file instead of uploading a new one?`);
          if (confirmed) {
              this.proceedWithExistingFile(existingFile);
          }
          return;
      }
  }

    this.checkingExistingFiles = true;
    try {
        const { exists, existingFile } = await this.checkIfFileExists(this.selectedFile, this.selectedCategory);
        if (exists && existingFile) {
            const confirmed = confirm(`A file with the same name and size already exists in the system. Do you want to use the existing file "${existingFile.file_name}" instead of uploading a new one?`);
            if (confirmed) {
                this.proceedWithExistingFile(existingFile);
                return;
            } else {
                this.uploadInProgress = false;
                this.checkingExistingFiles = false;
                return;
            }
        }
    } finally {
        this.checkingExistingFiles = false;
    }

    this.uploadInProgress = true;
    this.uploadError = false;

    const { make, model, serialRanges, pvc, serialNumbers, serialExclusions } = this.data;
    const file_name = this.selectedFile.name.replaceAll(/~|#|\[|\]|<|>|\*|'|!|@|\$|%|\^|&|\(|\)|=|\+|{|}|\||\\|;|:|\"|,|<|>|\?|\/|`|~/g, ''); // This line will remove all the BDA disallowed characters and spaces from the file name before uploading
    const file_type = this.selectedCategory;
    const locale = 'en_US';

    // Potentially check here if there is already a file with that name 
    const urlParams = new URLSearchParams({
      make,
      model,
      file_name: file_name,
      locale,
      file_type,
      user
    });
    const apiUrl2 = environment.integrations.manualCheck;
    const urlWithParams = `${apiUrl2}?${urlParams.toString()}`;
    const response = await new Promise<any>((resolve, reject) => {
      this.httpRequestService.request<any>('GET', urlWithParams).subscribe({
        next: resolve,
        error: reject,
      });
    });

    let confirmed_file_name = file_name; // Default to original file name
    if (response.file_already_exists) {
      const splitted = file_name.split('.');
      confirmed_file_name = splitted[0] + '-v2' + '.' + splitted.slice(1).join('.');
      console.log(`A file with the name "${file_name}" already exists. Renaming file to "${confirmed_file_name}"`);
    }
    const uploadStartTime = new Date().toISOString();
    let lastS3Key: string | null = null;

    const isExactDuplicate = this.data.existingResources?.some((res) =>
      res.equipment_make === make &&
      res.equipment_model === model &&
      res.manualType === this.selectedCategory &&
      res.file_name === file_name &&
      serialRanges.some(range =>
        res.serialStart === range.from &&
        res.serialEnd === range.to
      )
    );

    if (isExactDuplicate) {
      this.validationMessage = 'This manual is already mapped to this serial range.';
      this.uploadInProgress = false;
      return;
    }

    const isReplacingAnother = this.data.existingResources?.some((res) =>
      res.equipment_make === make &&
      res.equipment_model === model &&
      res.manualType === this.selectedCategory &&
      res.file_name !== file_name &&
      serialRanges.some(range =>
        res.serialStart === range.from &&
        res.serialEnd === range.to
      )
    );

    if (isReplacingAnother) {
      const confirmed = confirm('There is already a manual mapped to this serial range and manual type. Are you sure you want to replace the existing manual?');
      if (!confirmed) {
        this.uploadInProgress = false;
        return;
      }
    }
    const apiUrl = environment.integrations.manualUpload;
    try {
      for (const range of serialRanges) {
        const filteredExclusions = (Array.isArray(serialExclusions) ? serialExclusions : []).filter((exclusion) => {
          if (typeof exclusion !== 'string') return false;
          if (exclusion.includes('-')) {
            const [start, end] = exclusion.split('-');
            return start && end && start >= range.from && end <= range.to;
          }
          return exclusion >= range.from && exclusion <= range.to;
        });

        const urlParams = new URLSearchParams({
          make,
          model,
          file_name: confirmed_file_name,
          start_serial: range.from,
          end_serial: range.to,
          file_type,
          locale,
          pvc,
          serialNumbers,
          serialExclusions: JSON.stringify(filteredExclusions),
          user
        });

        const urlWithParams = `${apiUrl}?${urlParams.toString()}`;

        const response = await new Promise<any>((resolve, reject) => {
          this.httpRequestService.request<any>('GET', urlWithParams).subscribe({
            next: resolve,
            error: reject,
          });
        });

        if (!response || !response.s3_key) {
          throw new Error('Invalid response from backend.');
        }

        lastS3Key = response.s3_key;

        if (!response.already_uploaded &&  response.url && response.fields) {
          const formData = new FormData();
          Object.entries(response.fields).forEach(([key, value]) => {
            formData.append(key, value as string);
          });
          formData.append('file', this.selectedFile, file_name);

          const uploadResponse = await fetch(response.url, {
            method: 'POST',
            body: formData,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed upload for serial range ${range.from} - ${range.to}`);
          }
        }
      }

      const uploadEndTime = new Date().toISOString();
      this.updateManualUploadStatus({
        object_key: lastS3Key || file_name, 
        manual_type: file_type!,
        uploaded_by: user,
        size_classifier: '',
        status_field: 'upload_success',      
        start_time: uploadStartTime,
        end_time: uploadEndTime,
        step: 'manual_upload',
        mode: 'admin frontend upload',
      });

      this.uploadInProgress = false;
      this.uploadCompleted = true;
      this.uploadDate = new Date().toLocaleDateString();
      this.uploadedURL = `${environment.integrations.manualDownload}?equipment_make=${make}&file_name=${encodeURIComponent(file_name)}&manual_type=${file_type}`;
    } catch (error) {
      console.error('Upload failed:', error);
      this.uploadInProgress = false;
      this.uploadError = true;
    }
  }

  private isFileAlreadyUploaded(file: File): boolean {
    return this.data.existingResources.some(res =>
      res.file_name === file.name &&
      res.file_size === file.size
    );
  }

  private async proceedWithExistingFile(existingFile: any): Promise<void> {
    const user = await this.getAuthenticatedUsername();
    this.uploadInProgress = true;
    this.uploadError = false;

    const { make, model, serialRanges, pvc, serialNumbers, serialExclusions } = this.data;
    const file_type = this.selectedCategory;
    const locale = existingFile.locale || 'en_US';
    const file_name = existingFile.file_name;

    try {
      for (const range of serialRanges) {
        const filteredExclusions = (Array.isArray(serialExclusions) ? serialExclusions : []).filter((exclusion) => {
          if (typeof exclusion !== 'string') return false;
          if (exclusion.includes('-')) {
            const [start, end] = exclusion.split('-');
            return start && end && start >= range.from && end <= range.to;
          }
          return exclusion >= range.from && exclusion <= range.to;
        });

        const urlParams = new URLSearchParams({
          make,
          model,
          file_name,
          start_serial: range.from,
          end_serial: range.to,
          file_type: file_type ?? '',
          locale,
          pvc,
          serialNumbers,
          serialExclusions: JSON.stringify(filteredExclusions),
          user
        });

        const urlWithParams = `${environment.integrations.manualUpload}?${urlParams.toString()}`;

        await new Promise<any>((resolve, reject) => {
          this.httpRequestService.request<any>('GET', urlWithParams).subscribe({
            next: resolve,
            error: reject,
          });
        });
      }

      this.uploadInProgress = false;
      this.uploadCompleted = true;
      this.uploadDate = new Date().toLocaleDateString();
      this.uploadedURL = existingFile.url;
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error using existing file:', error);
      this.uploadInProgress = false;
      this.uploadError = true;
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private async getFileSizeFromUrl(url: string): Promise<number> {
    if (!this.isValidUrl(url)) {
      return 0;
    }

    try {
      const headResponse = await lastValueFrom(
        this.http.head(url, { observe: 'response' })
      );

      const contentLength = headResponse.headers.get('content-length');
      return contentLength ? parseInt(contentLength, 10) : 0;
    } catch (error) {
      console.error('Error getting file size from URL:', url, error);
      return 0;
    }
  }

  private async checkIfFileExists(file: File, manual_type: String): Promise<{ exists: boolean, existingFile?: any }> {
    const sanitizedUrl = `${environment.integrations.manualDownload}?equipment_make=${this.data.make}&file_name=${encodeURIComponent(file.name)}&manual_type=${manual_type}`;

    try {
      const resource = await this.fetchDownloadUrl(sanitizedUrl);
      const cloudfrontUrl = resource.downloadUrl;
      const remoteSize = await this.getFileSizeFromUrl(cloudfrontUrl);

      if (remoteSize === file.size) {
        return {
          exists: true,
          existingFile: {
            file_name: file.name,
            file_size: file.size,
            url: cloudfrontUrl,
            locale: 'en_US',
            equipment_make: this.data.make,
            equipment_model: this.data.model,
            manualType: this.selectedCategory,
          }
        };
      }
    } catch (error) {
      console.warn('S3 check failed or file does not exist:', error);
    }
    return { exists: false };
  }
  

private async validatePdf(file: File): Promise<boolean> {

  this.validatingPdf = true;
  this.validationMessage = null;

  try {

    if (
      file.type !== 'application/pdf' &&
      file.type !== 'application/octet-stream'
    ) {
      this.validationMessage = 'File must be a PDF.';
      alert(this.validationMessage);
      return false;
    }

    if (!file || file.size < 5) {
      this.validationMessage = 'Corrupted or Invalid PDF file. Please Try again.';
      alert(this.validationMessage);
      return false;
    }

    const header = await this.readFileHeader(file, 5);
    const headerText = new TextDecoder().decode(header);

    if (!headerText.startsWith('%PDF-')) {
      this.validationMessage = 'The selected file is not recognized as a valid PDF.';
      alert(this.validationMessage);
      return false;
    }

    const arrayBuffer = await this.readFileAsArrayBuffer(file);

    const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    if (!pdf.numPages || pdf.numPages === 0) {
      this.validationMessage = 'The PDF has no pages and appears to be invalid. Please Try again.';
      alert(this.validationMessage);
      return false;
    }

    const firstPage = await pdf.getPage(1);
    if (!firstPage) {
      this.validationMessage = 'Corrupted or Invalid PDF file. Please Try again.';
      alert(this.validationMessage);
      return false;
    }
    return true;
  } catch (error: any) {
    console.error('PDF validation failed:', error);

    this.validationMessage =
      'Corrupted or Invalid PDF file. Please Try again.';

    alert(this.validationMessage);
    return false;
  } finally {
    this.validatingPdf = false;
  }
}

private readFileHeader(file: File, length: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const blob = file.slice(0, length);

    reader.onload = () => {
      if (!reader.result) {
        reject(new Error('Empty file result'));
        return;
      }
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    };

    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(blob);
  });
}

private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (!reader.result) {
        reject(new Error('Empty file result'));
        return;
      }
      resolve(reader.result as ArrayBuffer);
    };

    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
}

private updateManualUploadStatus(params: {
  object_key: string;
  manual_type: string;
  uploaded_by: string;
  size_classifier?: string;
  status_field: string;
  start_time: string;
  end_time: string;
  step: string;
  mode: string;
}): void {
  const baseUrl = environment.integrations.manualUploadStatus;

  const urlParams = new URLSearchParams({
    object_key: params.object_key,
    manual_type: params.manual_type,
    uploaded_by: params.uploaded_by,
    size_classifier: params.size_classifier ?? '',
    status_field: params.status_field,
    start_time: params.start_time,
    end_time: params.end_time,
    step: params.step,
    mode: params.mode,
  });

  const endpoint = `${baseUrl}?${urlParams.toString()}`;

  this.httpRequestService.request<any>('POST', endpoint).subscribe({
    next: () => { },
    error: (err) => console.error('Error updating manual upload status:', err),
  });
}

  resetForm() {
    this.selectedFile = null;
    this.selectedCategory = null;
    this.validationMessage = null;
    this.fileSelected = false;
    this.uploadCompleted = false;
    this.uploadError = false;
  }

  closeModal() {
    this.resetForm();
    this.dialogRef.close();
  }

  public openPDFModal(): void {
    if (this.uploadedURL) {
      this.fetchDownloadUrl(this.uploadedURL).then(resource => {
        this.dialog.open(ModalPdfComponent, {
          data: { pdfUrl: resource.downloadUrl }
        });
      }).catch(error => {
        console.error('Error fetching download URL:', error);
      });
    } else {
      console.error('Uploaded URL is null.');
    }
  }

  private fetchDownloadUrl(providerResourceUrl: string): Promise<{ downloadUrl: string, s3Key: string }> {
    return new Promise((resolve, reject) => {
      this.httpRequestService.request<any>('GET', providerResourceUrl).subscribe({
        next: (response) => resolve({ downloadUrl: response.download_url, s3Key: response.s3_key }),
        error: (error) => reject(error)
      });
    });
  }

  private async getAuthenticatedUsername(): Promise<string> {
    const userState = await firstValueFrom(this.store.select('user'));
    const email = userState.userInfo?.email;
    if (email) {
      return email.split('@')[0];
    }
    return 'anonymous';
  }
   private fetchCategories(): void {
    const url = environment.integrations.adminManualType; 
    this.httpRequestService.request<any>('GET', url).subscribe({
      next: (response) => {
        this.categories = response.manual_types || response.data || response || [];
      },
      error: (error) => {
        console.error('Error fetching categories:', error);
        this.categories = ['Service Manual', 'Operation Manual', 'Parts Manual', 'Operation & Maintenance Manual'];
      }
    });
  }
}